<script setup lang="ts">
import { computed } from 'vue';
import { normalizePreflightProfile, type PreflightProfileV2 } from './engine/preflight';
import { preflightOverlayVisible } from './uiState';
import { resolvePrintAreaPx, resolveViewPhysicalSizesCm, safeParseAreaJson, type PrintAreaRect } from '@designer/utils/printArea';
import { t } from './i18n';

const props = defineProps<{
  context: {
    viewName: string;
    config: Record<string, unknown>;
  };
}>();

// This overlay is mounted in the stage-wide overlay layer (the same coordinate
// space design-tools' rulers use), not scoped to the print-area box, so every
// position below is computed in stage pixels via the print area's own offset.
const itemsConfig = computed(() => (props.context.config.items ?? {}) as Record<string, unknown>);
const stageWidth = computed(() => Math.max(1, Number(itemsConfig.value.boxWidth) || 500));
const stageHeight = computed(() => Math.max(1, Number(itemsConfig.value.boxHeight) || 500));

const printArea = computed((): PrintAreaRect => {
  const rawArea = (itemsConfig.value.area as Record<string, unknown> | undefined)?.[props.context.viewName];
  const parsed = typeof rawArea === 'string' ? safeParseAreaJson(rawArea) : rawArea;
  return resolvePrintAreaPx(parsed as Record<string, unknown> | null, stageWidth.value, stageHeight.value)
    ?? { left: 0, top: 0, width: stageWidth.value, height: stageHeight.value };
});

const physicalSize = computed(
  () => resolveViewPhysicalSizesCm(itemsConfig.value.params as Record<string, string>)[props.context.viewName],
);

const preflightView = computed(() => {
  const profile = normalizePreflightProfile(itemsConfig.value.preflightProfile);
  const views = profile.views as Partial<Record<string, Partial<PreflightProfileV2>>> | undefined;
  return { ...profile, ...(views?.[props.context.viewName] ?? {}) };
});

function px(value: number): string {
  return `${value}px`;
}

function insetStyle(insetXPx: number, insetYPx: number) {
  const area = printArea.value;
  const x = Math.min(area.width * 0.49, insetXPx);
  const y = Math.min(area.height * 0.49, insetYPx);
  return {
    left: px(area.left + x),
    top: px(area.top + y),
    width: px(Math.max(0, area.width - 2 * x)),
    height: px(Math.max(0, area.height - 2 * y)),
  };
}

const safeOverlayStyle = computed(() => {
  const size = physicalSize.value;
  if (!size) return insetStyle(0, 0);
  const safety = Number(preflightView.value.safetyMm) || 0;
  const bleed = Number(preflightView.value.bleedMm) || 0;
  const area = printArea.value;
  return insetStyle(
    ((safety + bleed) / 10 / size.width) * area.width,
    ((safety + bleed) / 10 / size.height) * area.height,
  );
});

const trimOverlayStyle = computed(() => {
  const size = physicalSize.value;
  if (!size) return insetStyle(0, 0);
  const bleed = Number(preflightView.value.bleedMm) || 0;
  const area = printArea.value;
  return insetStyle(
    (bleed / 10 / size.width) * area.width,
    (bleed / 10 / size.height) * area.height,
  );
});

const preflightZoneOverlays = computed(() => (preflightView.value.zones ?? []).map((zone) => {
  const area = printArea.value;
  if (zone.polygon?.length) {
    const points = zone.polygon.map((p) => `${px(area.left + p.x * area.width)} ${px(area.top + p.y * area.height)}`);
    return {
      id: zone.id,
      label: zone.label,
      style: { left: px(area.left), top: px(area.top), width: px(area.width), height: px(area.height), clipPath: `polygon(${points.join(',')})` },
    };
  }
  const r = zone.rect;
  return {
    id: zone.id,
    label: zone.label,
    style: r
      ? { left: px(area.left + r.x * area.width), top: px(area.top + r.y * area.height), width: px(r.width * area.width), height: px(r.height * area.height) }
      : { display: 'none' },
  };
}));
</script>

<template>
  <template v-if="preflightOverlayVisible">
    <div class="vptw:pointer-events-none vptw:absolute vptw:z-[46] vptw:border vptw:border-dashed vptw:border-orange-500" :style="trimOverlayStyle">
      <span class="vptw:absolute vptw:left-1 vptw:top-0.5 vptw:text-[9px] vptw:font-semibold vptw:text-orange-700">{{ t('TRIM') }}</span>
    </div>
    <div class="vptw:pointer-events-none vptw:absolute vptw:z-[47] vptw:border vptw:border-dashed vptw:border-(--vs-accent)" :style="safeOverlayStyle">
      <span class="vptw:absolute vptw:right-1 vptw:top-0.5 vptw:text-[9px] vptw:font-semibold vptw:text-(--vs-accent)">{{ t('SAFE') }}</span>
    </div>
    <div v-for="zone in preflightZoneOverlays" :key="zone.id" class="vptw:pointer-events-none vptw:absolute vptw:z-[48] vptw:border vptw:border-red-500 vptw:bg-red-500/20" :style="zone.style" :title="zone.label" />
  </template>
</template>
